/**
 * ============================================================================
 * WHISKERY SOUR® - HEADER & FOOTER CLEANUP SCRIPT
 * Production-ready JavaScript for Minimog theme standardization
 * Cleans up duplicate DOM elements and ensures consistent structure
 * ============================================================================
 */

(function() {
  'use strict';

  // Configuration object for selectors and settings
  const CONFIG = {
    // Header selectors (adjust based on your actual Minimog theme class names)
    headerSelectors: [
      '.header-desktop',
      '[data-header-variant="desktop"]',
      '.site-header--desktop',
      'header.desktop-only'
    ],
    mobileHeaderSelectors: [
      '.header-mobile',
      '[data-header-variant="mobile"]',
      '.site-header--mobile',
      'header.mobile-only'
    ],
    // Search overlay selectors
    searchSelectors: [
      '.search-overlay',
      '[data-search-overlay]',
      '.search-modal',
      '.drawer--search'
    ],
    // Footer social link selectors
    socialSelectors: [
      '.footer-social-links',
      '[data-footer-section="social"]',
      '.footer__social'
    ],
    // Copyright/footer section selectors
    copyrightSelectors: [
      '.footer-copyright',
      '.footer__copyright',
      '[data-footer-section="copyright"]'
    ],
    // Announcement bar selectors
    announcementSelectors: [
      '.announcement-bar',
      '.header-announcement',
      '[data-section-type="announcement-bar"]'
    ],
    // Product section selectors
    productSectionSelectors: [
      '[data-section-type="recently-viewed"]',
      '[data-section-type="related-products"]'
    ],
    // Breakpoints
    breakpoints: {
      mobile: 768,
      desktop: 1024
    }
  };

  /**
   * Helper: Find all elements matching any selector in an array
   * @param {string[]} selectors - Array of CSS selectors
   * @returns {Element[]} Array of matching elements
   */
  function findElements(selectors) {
    const elements = [];
    selectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(el => {
          if (!elements.includes(el)) {
            elements.push(el);
          }
        });
      } catch (e) {
        // Selector might be invalid, skip gracefully
        console.warn(`Invalid selector: ${selector}`);
      }
    });
    return elements;
  }

  /**
   * Remove duplicate elements, keeping only the first one
   * @param {Element[]} elements - Array of elements
   * @param {string} reason - Reason for removal (for logging)
   */
  function removeDuplicates(elements, reason = 'duplicate') {
    if (elements.length <= 1) {
      return;
    }

    for (let i = 1; i < elements.length; i++) {
      const element = elements[i];
      if (element.parentNode) {
        element.remove();
        console.info(`Removed ${reason} element (${i}/${elements.length - 1})`);
      }
    }
  }

  /**
   * Handle header visibility based on viewport width
   * Ensures only one header version is rendered
   */
  function setupHeaderVisibility() {
    const desktopHeaders = findElements(CONFIG.headerSelectors);
    const mobileHeaders = findElements(CONFIG.mobileHeaderSelectors);

    function updateHeaderVisibility() {
      const isMobile = window.innerWidth < CONFIG.breakpoints.mobile;

      desktopHeaders.forEach(header => {
        header.style.display = isMobile ? 'none' : 'block';
        if (isMobile) {
          header.setAttribute('aria-hidden', 'true');
        } else {
          header.removeAttribute('aria-hidden');
        }
      });

      mobileHeaders.forEach(header => {
        header.style.display = isMobile ? 'block' : 'none';
        if (!isMobile) {
          header.setAttribute('aria-hidden', 'true');
        } else {
          header.removeAttribute('aria-hidden');
        }
      });
    }

    // Initial call
    updateHeaderVisibility();

    // Update on resize with debouncing
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateHeaderVisibility, 250);
    });
  }

  /**
   * Remove duplicate search overlays
   * Keeps only the first one
   */
  function cleanupSearchOverlays() {
    const searchElements = findElements(CONFIG.searchSelectors);
    removeDuplicates(searchElements, 'search overlay');

    // Ensure proper state management for remaining overlay
    if (searchElements.length > 0) {
      const overlay = searchElements[0];

      // Add dataset attribute to help track state
      overlay.setAttribute('data-search-state', 'initialized');

      // Ensure overlay can be toggled properly
      if (!overlay.hasAttribute('aria-hidden')) {
        overlay.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Remove duplicate footer social link blocks
   * Keeps only the first instance
   */
  function cleanupFooterSocials() {
    const socialElements = findElements(CONFIG.socialSelectors);
    removeDuplicates(socialElements, 'footer social links');
  }

  /**
   * Remove duplicate announcement bars
   * Keeps only the first one
   */
  function cleanupAnnouncementBars() {
    const announcements = findElements(CONFIG.announcementSelectors);
    removeDuplicates(announcements, 'announcement bar');
  }

  /**
   * Update copyright year to current year dynamically
   * Finds copyright text and replaces year placeholders
   */
  function updateCopyrightYear() {
    const currentYear = new Date().getFullYear();
    const copyrightElements = findElements(CONFIG.copyrightSelectors);

    copyrightElements.forEach(element => {
      if (element.textContent) {
        // Replace various year formats: 2025, [2025], © 2025, etc.
        let updated = false;

        // Pattern 1: © 2025 or © 2025-2026
        if (element.textContent.includes('202') && !element.textContent.includes(String(currentYear))) {
          const oldContent = element.textContent;
          element.textContent = oldContent.replace(/202\d(-202\d)?/g, currentYear);
          updated = true;
        }

        // Pattern 2: [year] placeholder
        if (element.textContent.includes('[') && element.textContent.includes(']')) {
          const oldContent = element.textContent;
          element.textContent = oldContent.replace(/\[\d{4}\]/g, currentYear);
          updated = true;
        }

        // Pattern 3: Smartly update range (e.g., 2020-2025 to 2020-2026)
        if (element.textContent.match(/(\d{4})-202\d/)) {
          const oldContent = element.textContent;
          element.textContent = oldContent.replace(/(\d{4})-202\d/g, `$1-${currentYear}`);
          updated = true;
        }

        if (updated) {
          console.info(`Updated copyright year to ${currentYear}`);
        }
      }

      // Also check for data attributes
      if (element.hasAttribute('data-copyright-year')) {
        element.setAttribute('data-copyright-year', currentYear);
      }
    });

    // Remove duplicate copyright blocks (keep first one)
    removeDuplicates(copyrightElements, 'copyright section');
  }

  /**
   * Remove duplicate product section blocks
   * Prevents 190+ duplicate DOM elements in recently viewed/related sections
   */
  function cleanupProductSections() {
    const productSections = findElements(CONFIG.productSectionSelectors);

    productSections.forEach(section => {
      // Get all product items within this section
      const productItems = section.querySelectorAll('[data-product-item], .product-item, [data-product-id]');

      // Track seen product IDs to find duplicates
      const seenIds = new Set();
      const duplicatesToRemove = [];

      productItems.forEach(item => {
        let productId = null;

        // Try to get product ID from various attributes
        if (item.hasAttribute('data-product-id')) {
          productId = item.getAttribute('data-product-id');
        } else if (item.dataset.productId) {
          productId = item.dataset.productId;
        } else {
          // Try to extract from href
          const link = item.querySelector('a[href*="/products/"]');
          if (link) {
            const match = link.href.match(/\/products\/([^/?]+)/);
            if (match) {
              productId = match[1];
            }
          }
        }

        if (productId) {
          if (seenIds.has(productId)) {
            duplicatesToRemove.push(item);
          } else {
            seenIds.add(productId);
          }
        }
      });

      // Remove duplicate product items
      duplicatesToRemove.forEach(item => {
        if (item.parentNode) {
          item.remove();
          console.info(`Removed duplicate product item from section`);
        }
      });
    });
  }

  /**
   * Defer non-critical header scripts
   * Improves initial page load performance
   */
  function deferNonCriticalScripts() {
    const scripts = document.querySelectorAll('script[data-defer-header]');

    scripts.forEach(script => {
      if (document.readyState === 'loading') {
        // Still loading, defer to after DOMContentLoaded
        script.setAttribute('async', 'async');
      } else {
        // Already loaded, load after a brief delay
        setTimeout(() => {
          if (script.src) {
            const newScript = document.createElement('script');
            newScript.src = script.src;
            newScript.async = true;
            document.head.appendChild(newScript);
            script.remove();
          }
        }, 500);
      }
    });
  }

  /**
   * Lazy-load header images
   * Improves initial page load performance
   */
  function lazyLoadHeaderImages() {
    const headers = findElements([...CONFIG.headerSelectors, ...CONFIG.mobileHeaderSelectors]);

    headers.forEach(header => {
      const images = header.querySelectorAll('img[data-lazy-src], img[data-lazy], img[loading="lazy"]');

      images.forEach(img => {
        // Handle data-lazy-src attribute (common in Shopify themes)
        if (img.hasAttribute('data-lazy-src')) {
          const lazySrc = img.getAttribute('data-lazy-src');
          if (!img.src || img.src === 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7') {
            img.src = lazySrc;
            img.removeAttribute('data-lazy-src');
          }
        }

        // Ensure loading="lazy" is set
        if (!img.hasAttribute('loading')) {
          img.loading = 'lazy';
        }

        // Add decoding hint for performance
        if (!img.hasAttribute('decoding')) {
          img.decoding = 'async';
        }
      });
    });
  }

  /**
   * Helper to ensure consistent nav structure
   * Validates and standardizes navigation element attributes
   */
  function standardizeNavStructure() {
    const navs = document.querySelectorAll('nav, [role="navigation"], .site-nav');

    navs.forEach(nav => {
      // Ensure nav has role
      if (!nav.hasAttribute('role')) {
        nav.setAttribute('role', 'navigation');
      }

      // Ensure nav lists are marked as lists
      const lists = nav.querySelectorAll('ul, ol, [role="list"]');
      lists.forEach(list => {
        if (!list.hasAttribute('role')) {
          list.setAttribute('role', 'list');
        }

        const items = list.querySelectorAll('li, [role="listitem"]');
        items.forEach(item => {
          if (!item.hasAttribute('role')) {
            item.setAttribute('role', 'listitem');
          }
        });
      });

      // Ensure nav links have proper attributes
      const links = nav.querySelectorAll('a');
      links.forEach(link => {
        if (!link.hasAttribute('aria-label') && link.textContent.trim().length === 0) {
          console.warn('Navigation link found without text or aria-label');
        }
      });
    });
  }

  /**
   * Remove duplicate footer sections
   * Comprehensive cleanup for all footer duplicates
   */
  function cleanupFooterSections() {
    // Remove duplicate copyright sections
    const copyrights = findElements(CONFIG.copyrightSelectors);
    removeDuplicates(copyrights, 'copyright');

    // Remove duplicate announcement bars if they appear in footer
    const announcements = findElements(CONFIG.announcementSelectors);
    removeDuplicates(announcements, 'announcement bar');

    // Clean up any duplicate footer containers
    const footers = document.querySelectorAll('footer, [data-section-type="footer"], .site-footer');
    if (footers.length > 1) {
      // Keep first footer, remove others
      const firstFooter = footers[0];
      for (let i = 1; i < footers.length; i++) {
        if (footers[i].parentNode && footers[i] !== firstFooter) {
          // Only remove if it's truly a duplicate
          if (footers[i].textContent === firstFooter.textContent) {
            footers[i].remove();
            console.info(`Removed duplicate footer element`);
          }
        }
      }
    }
  }

  /**
   * Performance observer for layout shift caused by cleanup
   * Helps identify if cleanup is causing CLS (Cumulative Layout Shift)
   */
  function monitorLayoutShifts() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              console.warn(`Layout shift detected: ${entry.value}`);
            }
          }
        });

        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // PerformanceObserver for layout-shift not supported
        console.debug('Layout Shift observation not supported');
      }
    }
  }

  /**
   * Validate cleanup completed successfully
   * Provides console feedback about what was cleaned
   */
  function validateCleanup() {
    console.group('Whiskery Sour® Header/Footer Cleanup Report');

    // Check for remaining duplicates
    const desktopHeaders = findElements(CONFIG.headerSelectors);
    const mobileHeaders = findElements(CONFIG.mobileHeaderSelectors);
    const searchOverlays = findElements(CONFIG.searchSelectors);
    const footerSocials = findElements(CONFIG.socialSelectors);
    const footers = document.querySelectorAll('footer, [data-section-type="footer"]');

    console.info(`Desktop headers found: ${desktopHeaders.length}`);
    console.info(`Mobile headers found: ${mobileHeaders.length}`);
    console.info(`Search overlays found: ${searchOverlays.length}`);
    console.info(`Footer social blocks found: ${footerSocials.length}`);
    console.info(`Footer sections found: ${footers.length}`);

    // Count DOM elements
    const totalElements = document.querySelectorAll('*').length;
    console.info(`Total DOM elements: ${totalElements}`);

    // Check copyright year
    const copyrights = findElements(CONFIG.copyrightSelectors);
    const currentYear = new Date().getFullYear();
    let validCopyrights = 0;
    copyrights.forEach(el => {
      if (el.textContent.includes(String(currentYear))) {
        validCopyrights++;
      }
    });
    console.info(`Copyright years updated: ${validCopyrights}/${copyrights.length}`);

    console.groupEnd();
  }

  /**
   * Main initialization function
   * Runs all cleanup operations
   */
  function init() {
    console.info('Initializing Whiskery Sour® Header/Footer cleanup...');

    try {
      // Perform all cleanup operations
      setupHeaderVisibility();
      cleanupSearchOverlays();
      cleanupFooterSocials();
      cleanupAnnouncementBars();
      updateCopyrightYear();
      cleanupFooterSections();
      cleanupProductSections();
      deferNonCriticalScripts();
      lazyLoadHeaderImages();
      standardizeNavStructure();

      // Performance monitoring
      monitorLayoutShifts();

      // Validation report
      validateCleanup();

      console.info('Whiskery Sour® Header/Footer cleanup complete ✓');
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  /**
   * Run cleanup when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  /**
   * Optional: Re-run cleanup for dynamically loaded content (e.g., AJAX sections)
   * Uncomment to enable if you have dynamic content loading
   */
  window.WhiskerySourCleanup = {
    // Re-run cleanup for dynamically loaded content
    refreshDynamicContent: function() {
      console.info('Re-running cleanup for dynamic content...');
      cleanupProductSections();
      cleanupFooterSections();
      lazyLoadHeaderImages();
      standardizeNavStructure();
      validateCleanup();
    },
    // Manual cleanup trigger
    forceCleanup: init,
    // Get current stats
    getStats: function() {
      return {
        totalElements: document.querySelectorAll('*').length,
        desktopHeaders: findElements(CONFIG.headerSelectors).length,
        mobileHeaders: findElements(CONFIG.mobileHeaderSelectors).length,
        searchOverlays: findElements(CONFIG.searchSelectors).length,
        footerSocials: findElements(CONFIG.socialSelectors).length
      };
    }
  };
})();

/**
 * ============================================================================
 * END OF HEADER & FOOTER CLEANUP SCRIPT
 * ============================================================================
 */